import { definePlugin } from '../../framework';
import { EntryType } from '../../../../types/src/schemas';

import type { UtilsApi } from '../../types';
import { predictIterator } from '../../definitions/iterators/iterator_forest';
import { detectTablesFromFullText, splitLines } from '../declare-items/tableLlmClient';

type IteratorSuggestion = { value: string; snippet: any; type: EntryType };

let cachedIteratorSuggestions: IteratorSuggestion[] = [];

let foundContainerTable = false;
export function hasContainerTableIterator(): boolean {
  return foundContainerTable;
}

export type ChosenIterator = { mapPath: string[]; value: unknown };

let chosenIterator: ChosenIterator | null = null;
let iteratorBootstrap: Promise<void> = Promise.resolve();

export function getChosenIterator(): ChosenIterator | null {
  return chosenIterator;
}

export function whenIteratorBootstrapped(): Promise<void> {
  return iteratorBootstrap;
}

export function readExistingIterator(
  mapPath: string[],
  getValue: ((path: string[]) => any) | undefined,
): ChosenIterator | null {
  const node = getValue?.([...mapPath, '#iterator']);
  if (!node?.isOk?.()) return null;

  const raw = node.value?.value;
  if (raw === undefined || raw === null || raw === '') return null;
  if (typeof raw !== 'string') return { mapPath, value: raw };
  try {
    return { mapPath, value: JSON.parse(raw) };
  } catch {
    return null;
  }
}

const ITERATOR_KEY = '#iterator';

/**
 * The iterator as it is in the template now. The remembered choice only
 * decides which map to prefer: it may have been edited or removed since,
 * and a re-evaluation without the Iterator plugin does not refresh it.
 */
export function readCurrentIterator(
  getPaths: (() => { isOk(): boolean; value?: string[][] }) | undefined,
  getValue: ((path: string[]) => any) | undefined,
  preferred: ChosenIterator | null,
): ChosenIterator | null {
  const current = preferred && readExistingIterator(preferred.mapPath, getValue);
  if (current) return current;

  const result = getPaths?.();
  const paths = result?.isOk() ? (result.value ?? []) : [];
  for (const path of paths) {
    if (path[path.length - 1] !== ITERATOR_KEY) continue;
    const found = readExistingIterator(path.slice(0, -1), getValue);
    if (found) return found;
  }
  return null;
}

const LABEL_MAP = [
  'count occurences',
  'get from list',
  'iterate table',
] as const;
type IteratorLabel = typeof LABEL_MAP[number];

export enum Domain {
  Intermodal = 'intermodal',
  Retail = 'retail',
  EasyEOM = 'easyeom',
}

function resolveDomain(input?: Domain): Domain {
  switch (input) {
    case Domain.Intermodal:
      return Domain.Intermodal;
    case Domain.Retail:
      return Domain.Retail;
    case Domain.EasyEOM:
      return Domain.EasyEOM;
    default:
      return Domain.Intermodal;
  }
}

type IteratorState = {
  domain?: Domain;
};

type IteratorResult = {
  tableIndex: number;
  title: string;
  iterator: IteratorLabel;
  probs: [number, number, number];
  startLine: number;
  endLine: number;
  rows: number;
  cols: number;
  occurences?: Array<{ combo: string; count: number; col?: number }>;
  mapKey: string;
  fromLabel?: string;
  toLabel?: string;
};

function mkSnippet(obj: any) {
  return new Proxy(obj ?? {}, {
    get(target, prop, receiver) {
      const json = JSON.stringify(target);

      if (prop === 'slice') {
        return (start?: number, end?: number) => json.slice(start, end);
      }

      if (prop === 'substring') {
        return (start: number, end?: number) => json.substring(start, end);
      }
      if (prop === 'substr') {
        return (start: number, length?: number) =>
          (json as any).substr(start, length);
      }
      if (prop === 'toString') {
        return () => json;
      }
      if (prop === 'valueOf') {
        return () => json;
      }
      if (prop === Symbol.toPrimitive) {
        return () => json;
      }
      if (prop === 'length') {
        return json.length;
      }

      return Reflect.get(target, prop, receiver);
    },
  }) as any;
}

async function extractAnchorHeadersWithLLM(args: {
  textContent: string;
  extractWithLLM: UtilsApi['extractWithLLM'];
}): Promise<string[]> {
  const { textContent, extractWithLLM } = args;

  const MAX_CHARS = 60_000;
  const clipped =
    textContent.length > MAX_CHARS
      ? textContent.slice(0, MAX_CHARS)
      : textContent;

  try {
    const response = await extractWithLLM(clipped, 'Get_Iterator');
    if (response.isErr()) {
      return [];
    }

    const text = await response.value.text;

    let parsed: any;
    try {
      parsed = JSON.parse(text);
    } catch {
      const start = text.indexOf('[');
      const end = text.lastIndexOf(']');
      if (start !== -1 && end !== -1 && end > start) {
        try {
          parsed = JSON.parse(text.slice(start, end + 1));
        } catch {
          return [];
        }
      } else {
        return [];
      }
    }

    if (!Array.isArray(parsed)) {
      return [];
    }

    const norm = (s: string) => s.toLowerCase().replace(/[\s._:-]+/g, '');
    const normText = norm(textContent);

    const anchors = parsed
      .filter((x: any) => typeof x === 'string')
      .map((s: string) => s.trim())
      .filter((s: string) => s.length >= 3 && s.length <= 60)
      .filter((s: string) => /[A-Za-z\p{L}]/u.test(s))
      .filter((s: string) => normText.includes(norm(s)));

    if (!anchors.length) {
      return [];
    }

    const lower = textContent.toLowerCase();
    const count = (needle: string) => {
      const n = needle.toLowerCase();
      let i = 0,
        c = 0;
      while (true) {
        i = lower.indexOf(n, i);
        if (i === -1) break;
        c++;
        i += n.length;
      }
      return c;
    };

    return anchors
      .map((a) => ({ a, c: count(a) }))
      .sort((x, y) => y.c - x.c || y.a.length - x.a.length)
      .map((x) => x.a)
      .slice(0, 10);
  } catch {
    return [];
  }
}

type ContainerTableRange = { from: string; to: string };

type ContainerDetectionResult = {
  table: ContainerTableRange | null;
  countOccurenceAnchors: string[];
  expectedContainerCount: number | null;
};

function buildTableIteratorSnippet(from: string, to: string) {
  return mkSnippet({
    table: {
      from,
      to,
      findByColumns: false,
      fromline: 0,
      columns: {
        '#$composeTableColumns': {
          from,
          fromLine: 0,
          lines: 1,
        },
      },
      skip: {
        '$utils.not': {
          '#': '#>$column#0',
        },
      },
    },
  });
}

function buildContainerTableMorphJson(containerTable: ContainerTableRange) {
  return JSON.stringify({
    table: {
      from: containerTable.from,
      to: containerTable.to,
      findByColumns: false,
      fromline: 0,
      columns: {
        '#$composeTableColumns': {
          from: containerTable.from,
          fromLine: 0,
          lines: 1,
        },
      },
      skip: {
        '$utils.not': {
          '#': '#>$column#0',
        },
      },
    }
  });
}

async function detectContainerTable(args: {
  textContent: string;
  extractWithLLM: UtilsApi['extractWithLLM'];
  promptName: string;
  retryInstruction?: string;
}): Promise<ContainerDetectionResult | null> {
  const { textContent, extractWithLLM, promptName, retryInstruction } = args;

  const MAX_CHARS = 60_000;
  const clipped =
    textContent.length > MAX_CHARS
      ? textContent.slice(0, MAX_CHARS)
      : textContent;
  const content = retryInstruction ? retryInstruction + clipped : clipped;

  try {
    const response = await extractWithLLM(content, promptName);
    if (response.isErr()) {
      return null;
    }

    let text = (await response.value.text).trim();

    if (text.startsWith('[')) {
      try {
        const messages = JSON.parse(text) as Array<{
          role: string;
          content?: string;
        }>;
        const lastWithContent = [...messages]
          .reverse()
          .find((m) => m.content);
        if (lastWithContent?.content) text = lastWithContent.content.trim();
      } catch {
      }
    }

    const start = text.indexOf('{');
    const end = text.lastIndexOf('}');
    const jsonSlice =
      start !== -1 && end !== -1 ? text.slice(start, end + 1) : text;

    let parsed: any;
    try {
      parsed = JSON.parse(jsonSlice);
    } catch {
      return null;
    }

    const from = typeof parsed?.from === 'string' ? parsed.from.trim() : '';
    const to = typeof parsed?.to === 'string' ? parsed.to.trim() : '';
    const countOccurenceAnchors = Array.isArray(parsed?.countOccurenceAnchors)
      ? parsed.countOccurenceAnchors
          .filter((a: unknown): a is string => typeof a === 'string')
          .map((a: string) => a.trim())
          .filter((a: string) => a.length > 0)
      : [];
    const expectedContainerCount =
      typeof parsed?.expectedContainerCount === 'number' &&
      Number.isFinite(parsed.expectedContainerCount) &&
      parsed.expectedContainerCount > 0
        ? Math.round(parsed.expectedContainerCount)
        : null;

    const table = parsed?.found && from && to ? { from, to } : null;

    return { table, countOccurenceAnchors, expectedContainerCount };
  } catch {
    return null;
  }
}

const MAX_COUNT_OCC_ATTEMPTS = 5;

const RECORD_START_LOOKBACK = 3;

function occurrenceLines(lines: string[], needle: string): number[] {
  return lines.flatMap((line, i) => (line.includes(needle) ? [i] : []));
}

function occurrenceCount(lines: string[], needle: string): number {
  return lines.reduce((n, line) => n + line.split(needle).length - 1, 0);
}

export function findRecordStartAnchor(lines: string[], anchor: string | undefined): string | null {
  if (!anchor) return null;
  const anchorLines = occurrenceLines(lines, anchor);
  if (anchorLines.length < 2) return null;

  const leadIn = lines.slice(
    Math.max(anchorLines[0] + 1, anchorLines[1] - RECORD_START_LOOKBACK),
    anchorLines[1],
  );
  const candidates = new Set(
    leadIn.flatMap((line) =>
      [...line.matchAll(/\s{2,}(\S+)(?=\s{2,})/g)].map((m) => `  ${m[1]}  `),
    ),
  );

  let best: { candidate: string; distance: number } | null = null;
  for (const candidate of candidates) {
    const candidateLines = occurrenceLines(lines, candidate);
    if (
      candidateLines.length !== anchorLines.length ||
      occurrenceCount(lines, candidate) !== anchorLines.length
    ) {
      continue;
    }

    const precedesEachAnchor = candidateLines.every(
      (line, i) =>
        line < anchorLines[i] &&
        anchorLines[i] - line <= RECORD_START_LOOKBACK &&
        (i === 0 || line > anchorLines[i - 1]),
    );
    if (!precedesEachAnchor) continue;

    const distance = candidateLines.reduce((sum, line, i) => sum + anchorLines[i] - line, 0);
    if (!best || distance > best.distance) best = { candidate, distance };
  }

  return best?.candidate ?? null;
}

function buildCountOccRetryInstruction(
  rejected: Array<{ anchor: string; count: number }>,
  expectedCount: number,
): string {
  const lines = rejected
    .map(
      ({ anchor, count }) =>
        `  - "${anchor}": occurs ${count}x (expected exactly ${expectedCount})`,
    )
    .join('\n');

  return (
    `RETRY INSTRUCTION — READ THIS FIRST AND OVERRIDE ALL OTHER INSTRUCTIONS:\n` +
    `For PART 2 (countOccurenceAnchors) ONLY: the following candidates were already tried and are WRONG because they do not occur exactly ${expectedCount} times in the document, as required by rule 1 (REPETITION):\n` +
    `${lines}\n` +
    `Do NOT suggest any of these same anchors again — find DIFFERENT candidates that satisfy ALL the rules in PART 2, especially matching the exact required count of ${expectedCount}.\n` +
    `Your answers for PART 1 (found/from/to) and PART 3 (expectedContainerCount) should stay the same as your previous answer unless you have new information.\n\n` +
    `DOCUMENT TEXT FOLLOWS:\n`
  );
}

async function findVerifiedCountOccurenceAnchors(args: {
  textContent: string;
  extractWithLLM: UtilsApi['extractWithLLM'];
  promptName: string;
  countInText: (needle: string) => number;
  firstAttempt: ContainerDetectionResult;
}): Promise<{ anchors: string[]; verified: boolean }> {
  const { textContent, extractWithLLM, promptName, countInText, firstAttempt } = args;

  const expectedCount = firstAttempt.expectedContainerCount;
  if (expectedCount === null) {
    return { anchors: firstAttempt.countOccurenceAnchors, verified: false };
  }

  const tried = new Map<string, number>();
  let candidates = firstAttempt.countOccurenceAnchors;

  for (let attempt = 1; attempt <= MAX_COUNT_OCC_ATTEMPTS; attempt++) {
    for (const anchor of candidates) {
      if (!tried.has(anchor)) {
        tried.set(anchor, countInText(anchor));
      }
    }

    const verified = [...tried.entries()]
      .filter(([, count]) => count === expectedCount)
      .map(([anchor]) => anchor);

    if (verified.length > 0) {
      return { anchors: verified, verified: true };
    }

    if (attempt === MAX_COUNT_OCC_ATTEMPTS) break;

    const retryInstruction = buildCountOccRetryInstruction(
      [...tried.entries()].map(([anchor, count]) => ({ anchor, count })),
      expectedCount,
    );

    const retryResult = await detectContainerTable({
      textContent,
      extractWithLLM,
      promptName,
      retryInstruction,
    });

    candidates = retryResult?.countOccurenceAnchors ?? [];
  }

  if (tried.size === 0) return { anchors: [], verified: false };

  const [closestAnchor] = [...tried.entries()].sort(
    (a, b) => Math.abs(a[1] - expectedCount) - Math.abs(b[1] - expectedCount),
  )[0];

  return { anchors: [closestAnchor], verified: false };
}

export const useIterator = definePlugin<IteratorState>({
  // Without this the framework falls back to 'unknown', which is what its logs
  // and progress were previously keyed and labelled by.
  name: 'Iterator',
  version: '0.2.3',
  initialState: { domain: undefined },

  bootstrap: async ({
    state,
    log,
    progress,
    addMapIterator,
    extractWithLLM,
    ...ctx
  }) => {
    chosenIterator = null;
    let finishBootstrap!: () => void;
    iteratorBootstrap = new Promise<void>((resolve) => {
      finishBootstrap = resolve;
    });

    try {
      log('[Bootstrap] Iterator plugin...');
      progress(2, 'Starting');
      foundContainerTable = false;

      const textContent = (ctx as any)?.textContent as unknown;
      if (typeof textContent !== 'string' || !textContent.trim()) {
        progress(100, 'No text in document');
        return;
      }

      const domain = resolveDomain(state.domain);

      const containerDetectionPromptName =
        domain === Domain.Intermodal
          ? 'Iterator_Intermodal_Container'
          : domain === Domain.Retail
            ? 'Iterator_Retail_Load'
            : null;

      if (containerDetectionPromptName === null) {
        progress(15, 'Checking for a repeating table');
        const repeatCheck = await hasRepeatingTable({ textContent, extractWithLLM });

        if (!repeatCheck.hasRepeatingTable) {
          cachedIteratorSuggestions = [];
          progress(100, 'No repeating table found');
          return;
        }
      }

      const lines = splitLines(textContent);

      let results: IteratorResult[];
      try {
        progress(35, 'Finding tables');
        const detected = await detectTablesFromFullText({ textContent, extractWithLLM });
        const llmResults = detected.tables.length > 0
          ? processDetectedRanges(lines, detected.tables)
          : detectIteratorsFromLines(lines);
        results = llmResults.map((it) => ({ ...it, mapKey: it.mapKey ?? 'containers' }));
      } catch {
        results = detectIteratorsFromLines(lines).map((it) => ({ ...it, mapKey: it.mapKey ?? 'containers' }));
      }

      const allOccs = (results ?? [])
        .flatMap((r) => r.occurences ?? [])
        .filter((o) => (o.combo ?? '').trim().length > 0);

      allOccs.sort((a, b) => (b.count ?? 0) - (a.count ?? 0));

      const seen = new Set<string>();
      const uniqueOccs = allOccs.filter((o) => {
        const key = `${o.combo}@@${typeof o.col === 'number' ? o.col : ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      let anchors: string[] = [];
      if (containerDetectionPromptName === null) {
        progress(60, 'Finding item headers');
        anchors = await extractAnchorHeadersWithLLM({ textContent, extractWithLLM });

        if (!anchors.length) {
          const textOccs = countOccurrencesFromTextContent(textContent);
          anchors = textOccs.map((o) => o.combo).slice(0, 10);
        }
        if (!anchors.length) {
          anchors = uniqueOccs.map((o) => o.combo).slice(0, 10);
        }
      }

      const defaultCountOcc = anchors[0] ?? '';

      const defaultIteratorMorph = JSON.stringify({
        countoccurences: defaultCountOcc,
        referBack: '\n',
      });

      const lowerText = textContent.toLowerCase();
      const countInText = (needle: string): number => {
        const n = needle.toLowerCase();
        let i = 0, c = 0;
        while ((i = lowerText.indexOf(n, i)) !== -1) { c++; i += n.length; }
        return c;
      };

      const containerDetection = containerDetectionPromptName
        ? await detectContainerTable({
            textContent,
            extractWithLLM,
            promptName: containerDetectionPromptName,
          })
        : null;

      if (
        containerDetectionPromptName !== null &&
        containerDetection?.expectedContainerCount !== null &&
        containerDetection?.expectedContainerCount !== undefined &&
        containerDetection.expectedContainerCount <= 1
      ) {
        cachedIteratorSuggestions = [];
        return;
      }

      const containerTable = containerDetection?.table ?? null;
      foundContainerTable = Boolean(containerTable);
      const { anchors: verifiedCountOccAnchors, verified: containerCountOccVerified } =
        containerDetection && containerDetectionPromptName
          ? await findVerifiedCountOccurenceAnchors({
              textContent,
              extractWithLLM,
              promptName: containerDetectionPromptName,
              countInText,
              firstAttempt: containerDetection,
            })
          : { anchors: [] as string[], verified: false };

      const recordStart = containerCountOccVerified
        ? findRecordStartAnchor(lines, verifiedCountOccAnchors[0])
        : null;
      const containerCountOccAnchors = recordStart
        ? [recordStart, ...verifiedCountOccAnchors]
        : verifiedCountOccAnchors;

      let iterateTableSuggestions: IteratorSuggestion[];

      if (containerTable) {
        iterateTableSuggestions = [
          {
            value: 'Table',
            snippet: buildTableIteratorSnippet(containerTable.from, containerTable.to),
            type: EntryType.Code,
          },
        ];
      } else if (containerDetectionPromptName !== null) {
        iterateTableSuggestions = [];
      } else {
        const scoredTableResults = [...results]
          .filter((it) => (it.fromLabel ?? '').trim().length > 0)
          .sort((a, b) => b.probs[2] - a.probs[2]);

        iterateTableSuggestions = scoredTableResults.map((it, idx) => ({
          value: scoredTableResults.length === 1 ? 'table' : `table ${idx + 1}: "${it.fromLabel ?? ''}"`,
          snippet: buildTableIteratorSnippet(it.fromLabel ?? '', it.toLabel ?? ''),
          type: EntryType.Code,
        }));
      }

      const genericCountOccSuggestionsText: IteratorSuggestion[] = anchors
        .map((a) => ({ anchor: a, count: countInText(a) }))
        .filter((a) => a.count >= 2)
        .sort((a, b) => b.count - a.count)
        .map(({ anchor }) => ({
          value: `Occurence: ${anchor}`,
          snippet: mkSnippet({ countoccurences: anchor, referBack: '\n' }),
          type: EntryType.Code,
        }));

      const containerCountOccSuggestions: IteratorSuggestion[] = containerCountOccAnchors
        .map((anchor) => ({ anchor, count: countInText(anchor) }))
        .sort((a, b) => b.count - a.count)
        .map(({ anchor }) => ({
          value: `Occurence: ${anchor}`,
          snippet: mkSnippet({ countoccurences: anchor, referBack: '\n' }),
          type: EntryType.Code,
        }));

      const countOccSuggestionsText: IteratorSuggestion[] =
        containerCountOccSuggestions.length > 0
          ? containerCountOccSuggestions
          : containerDetectionPromptName !== null
            ? []
            : genericCountOccSuggestionsText;

      const countOccOutranksTable =
        containerCountOccVerified && countOccSuggestionsText.length > 0;

      cachedIteratorSuggestions = containerTable
        ? countOccOutranksTable
          ? [...countOccSuggestionsText, ...iterateTableSuggestions]
          : [...iterateTableSuggestions, ...countOccSuggestionsText]
        : [...countOccSuggestionsText, ...iterateTableSuggestions];

      let mapPath: string[];
      switch (domain) {
        case Domain.Retail:
          mapPath = ['root', 'template'];
          break;
        case Domain.EasyEOM:
        case Domain.Intermodal:
        default:
          mapPath = ['root', 'template', 'doc', 'containers'];
          break;
      }

      const skipDedicatedIterator =
        containerDetectionPromptName !== null &&
        !containerTable &&
        containerCountOccAnchors.length === 0;

      if (typeof addMapIterator === 'function' && !skipDedicatedIterator) {
        const it = results?.[0];

        const iteratorValue = countOccOutranksTable
            ? JSON.stringify({
                countoccurences: containerCountOccAnchors[0],
                referBack: '\n',
              })
            : containerTable
              ? buildContainerTableMorphJson(containerTable)
              : containerCountOccAnchors.length > 0
                ? JSON.stringify({
                    countoccurences: containerCountOccAnchors[0],
                    referBack: '\n',
                  })
                : defaultCountOcc
                  ? defaultIteratorMorph
                  : buildIteratorMorphJson(it);

        const res = addMapIterator(mapPath, EntryType.Code, {
          value: iteratorValue,
        });

        if (res.isOk()) {
          chosenIterator = { mapPath, value: JSON.parse(iteratorValue) };
        }
      }

      chosenIterator ??= readExistingIterator(mapPath, (ctx as any).getValue);
    } finally {
      // In finally so an early return or a thrown error still settles the bar,
      // rather than leaving it stuck partway through.
      progress(100, 'Done');
      finishBootstrap();
    }
  },
  fieldSuggestions({ entry }) {
    if (entry.key !== '#iterator') return [];
    return cachedIteratorSuggestions ?? [];
  },
});

function buildIteratorMorphJson(it: {
  iterator: IteratorLabel;
  occurences?: Array<{ combo: string; count: number; col?: number }>;
  fromLabel?: string;
  toLabel?: string;
}) {
  if (it.iterator === 'count occurences') {
    const best =
      it.occurences?.find((o) => (o.combo ?? '').trim().length > 0) ??
      it.occurences?.[0];
    return JSON.stringify({
      countoccurences: best?.combo ?? '',
      referBack: '\n',
    });
  }

  if (it.iterator === 'get from list') {
    return JSON.stringify({
      getFromList: '',
      referBack: '\n',
    });
  }

  return JSON.stringify({
    table: {
      from: it.fromLabel ?? '',
      to: it.toLabel ?? '',
      findByColumns: false,
      fromline: 0,
      columns: {
        '#$composeTableColumns': {
          from: it.fromLabel ?? '',
          fromLine: 0,
          lines: 1,
        },
      },
    },
    referBack: '\n',
  });
}

interface FeatureSet {
  is_blank: number;
  leading_spaces: number;
  first_char: number;
  last_char: number;
  all_special_char: number;
  seg2_spaces: number;
  seg3_spaces: number;
}

interface LabeledLine {
  index: number;
  text: string;
  prediction: 'TABLE' | 'NOT TABLE';
}

interface TableLine {
  text: string;
  index?: number;
  prediction?: 'TABLE' | 'NOT TABLE';
}

interface TableFeatures {
  num_rows: number;
  num_columns: number;
  avg_tokens_per_row: number;
  avg_tokens_per_column: number;
  std_dev_column_count: number;
  std_dev_row_count: number;
  percent_single_token_rows: number;
  percent_single_token_columns: number;
  percent_numeric_only_rows: number;
  percent_numeric_only_columns: number;
  percent_rows_with_numeric_prefix: number;
  percent_columns_with_numeric_prefix: number;
  avg_double_space_count_per_row: number;
  columns_with_consistent_word: number;
  rows_with_consistent_word: number;
  most_frequent_keyword: string | null;
  max_word_freq_in_column: number;
  max_word_freq_in_row: number;
}

function inferMapKeyFromTitle(title: string): string {
  const t = title.toLowerCase();
  if (t.includes('container')) return 'containers';
  if (t.includes('charge')) return 'charges';
  if (t.includes('item')) return 'items';
  return 'tables';
}
function extractLabel(line: string): string {
  const s = line.trim();
  if (!s) return '';

  const m = s.match(/^(.{1,80}?):\s*/);
  if (m) return m[1].trim();

  const chunk = s.split(/\s{2,}/)[0]?.trim() ?? '';
  return chunk.length <= 80 ? chunk : '';
}

function inferFromToFromBlock(block: TableLine[]): {
  from: string;
  to: string;
} {
  const labels = block.map((l) => extractLabel(l.text)).filter(Boolean);

  return {
    from: labels[0] ?? '',
    to: labels.at(-1) ?? '',
  };
}

function processDetectedRanges(
  lines: string[],
  ranges: Array<{ startLine: number; endLine: number }>,
): IteratorResult[] {
  const allLabeledLines: LabeledLine[] = lines.map((text, i) => ({
    index: i + 1,
    text,
    prediction: 'NOT TABLE' as const,
  }));

  return ranges.flatMap((range, idx) => {
    const block: LabeledLine[] = lines
      .slice(range.startLine, range.endLine + 1)
      .map((text, i) => ({
        index: range.startLine + i + 1,
        text,
        prediction: 'TABLE' as const,
      }));

    if (block.length === 0) return [];

    const title = extractTitle(allLabeledLines, block).trim();

    const cleanedBlock = block.filter((l) => {
      const s = l.text.trim();
      return !(title.includes(s) || s.includes(title));
    });

    const workBlock = cleanedBlock.length > 0 ? cleanedBlock : block;

    const { from: cleanedFrom, to } = inferFromToFromBlock(workBlock);
    const from = extractLabel(block[0]?.text ?? '') || cleanedFrom;
    const columns = computeColumnPositions(workBlock);
    const rawFeatures = extractTableFeatures(workBlock);
    const features = flattenFeatures(rawFeatures);
    const co = CountOccurrences(workBlock, columns);

    let iterator: IteratorLabel;
    let probs: [number, number, number];

    if (features && features.length === 17 && !features.some((v) => v === undefined || Number.isNaN(v))) {
      probs = predictIterator(features) as [number, number, number];
      const maxIndex = probs.indexOf(Math.max(...probs));
      iterator = co.matched ? 'count occurences' : LABEL_MAP[maxIndex];
    } else {
      probs = [0, 0, 1];
      iterator = co.matched ? 'count occurences' : 'iterate table';
    }

    return [{
      tableIndex: idx + 1,
      title,
      iterator,
      probs,
      startLine: range.startLine,
      endLine: range.endLine,
      rows: rawFeatures?.num_rows ?? workBlock.length,
      cols: rawFeatures?.num_columns ?? columns.length,
      occurences: co.matched ? co.combos : undefined,
      mapKey: inferMapKeyFromTitle(title),
      fromLabel: from,
      toLabel: to,
    }];
  });
}

function detectIteratorsFromLines(
  lines: string[]
): IteratorResult[] {
  const labeledLines: LabeledLine[] = lines.map((line, i) => ({
    index: i + 1,
    text: line,
    prediction:
      decisionTreeClassifier(extractFeaturesFromLine(line), line) === 1
        ? 'TABLE'
        : 'NOT TABLE',
  }));

  const tableBlocks = groupTableBlocks(labeledLines);

  const filteredBlocks = tableBlocks.filter((block) => {
    const totalLen = block.reduce((sum, l) => sum + l.text.trim().length, 0);
    const avgLen = totalLen / block.length;
    return avgLen < 150;
  });

  const out: IteratorResult[] = [];

  filteredBlocks.forEach((block, idx) => {
    const title = extractTitle(labeledLines, block).trim();

    const cleanedBlock = block.filter((line) => {
      const t = title;
      const s = line.text.trim();
      return !(t.includes(s) || s.includes(t));
    });

    const { from, to } = inferFromToFromBlock(cleanedBlock);

    const columns = computeColumnPositions(cleanedBlock);
    const rawFeatures = extractTableFeatures(cleanedBlock);
    const features = flattenFeatures(rawFeatures);

    if (
      !features ||
      features.length !== 17 ||
      features.some((v) => v === undefined || Number.isNaN(v))
    ) {
      return;
    }

    const probs = predictIterator(features) as [number, number, number];
    const maxIndex = probs.indexOf(Math.max(...probs));

    const co = CountOccurrences(cleanedBlock, columns);
    let iterator: IteratorLabel = LABEL_MAP[maxIndex];

    if (co.matched) iterator = 'count occurences';
    const occurences = co.matched ? co.combos : undefined;

    const mapKey = inferMapKeyFromTitle(title);

    out.push({
      tableIndex: idx + 1,
      title,
      iterator,
      probs,
      startLine: cleanedBlock[0]?.index ?? block[0].index,
      endLine: cleanedBlock.at(-1)?.index ?? block.at(-1)!.index,
      rows: rawFeatures?.num_rows ?? cleanedBlock.length,
      cols: rawFeatures?.num_columns ?? columns.length,
      occurences,
      mapKey,
      fromLabel: from,
      toLabel: to,
    });
  });

  return out;
}

function extractFeaturesFromLine(text: string): FeatureSet {
  const is_blank = text.trim() === '' ? 1 : 0;
  const leading_spaces = text.length - text.trimStart().length;
  const first_char = text.trim()[0]?.charCodeAt(0) ?? 0;
  const last_char =
    text
      .trim()
      .slice(-1)
      ?.charCodeAt(text.trim().length - 1) ?? 0;
  const all_special_char = /^[^a-zA-Z0-9]+$/.test(text.trim()) ? 1 : 0;
  const tokens = text.trim().split(/\s+/);
  const seg2_spaces = tokens.length > 1 ? tokens.length - 1 : 0;
  const seg3_spaces = (text.match(/\s{2,}/g) || []).length;
  return {
    is_blank,
    leading_spaces,
    first_char,
    last_char,
    all_special_char,
    seg2_spaces,
    seg3_spaces,
  };
}

function decisionTreeClassifier(f: FeatureSet, fullText: string): number {
  const cells = fullText.trim().split(/\s{2,}/);
  if (cells.length > 1 && cells[1].length > 30) return 0;

  if (f.seg3_spaces <= 0.5) {
    if (f.is_blank <= 0.5) return 0;
    return 0;
  } else {
    if (f.leading_spaces <= 2.5) return 1;
    if (f.leading_spaces <= 21.5) return f.seg2_spaces <= 1.5 ? 0 : 1;
    return 1;
  }
}

function groupTableBlocks(labeledLines: LabeledLine[]): LabeledLine[][] {
  const tableBlocks: LabeledLine[][] = [];
  let currentBlock: LabeledLine[] = [];
  let gapCount = 0;

  labeledLines.forEach((line) => {
    if (line.prediction === 'TABLE') {
      if (gapCount >= 2 && currentBlock.length > 0) {
        tableBlocks.push(currentBlock);
        currentBlock = [];
      }
      currentBlock.push(line);
      gapCount = 0;
    } else {
      gapCount++;
      if (currentBlock.length > 0 && gapCount >= 2) {
        tableBlocks.push(currentBlock);
        currentBlock = [];
      }
    }
  });

  if (currentBlock.length > 0) tableBlocks.push(currentBlock);
  return tableBlocks;
}

function extractTitle(
  labeledLines: LabeledLine[],
  tableBlock: LabeledLine[]
): string {
  const blockStartIndex = tableBlock[0].index;
  const potentialTitles = labeledLines
    .filter((line) => line.index < blockStartIndex && line.text.trim() !== '')
    .slice(-3);

  for (let i = potentialTitles.length - 1; i >= 0; i--) {
    const lineText = potentialTitles[i].text.trim();
    if (
      lineText === lineText.toUpperCase() &&
      lineText.length < 50 &&
      !/\s{2,}/.test(lineText)
    ) {
      return lineText;
    }
  }

  const firstLine = tableBlock[0]?.text.trim();
  return firstLine?.split(/\s{2,}/)[0] || 'Untitled Table';
}

function computeColumnPositions(block: TableLine[], tolerance = 2): number[] {
  const allGaps: number[] = [];

  block.forEach((line) => {
    [...line.text.matchAll(/ {2,}/g)].forEach((match) => {
      const pos = (match.index ?? 0) + match[0].length;
      allGaps.push(pos);
    });
  });

  allGaps.sort((a, b) => a - b);

  const clusters: number[][] = [];
  let current: number[] = [];

  for (let i = 0; i < allGaps.length; i++) {
    if (
      current.length === 0 ||
      Math.abs(allGaps[i] - current[current.length - 1]) <= tolerance
    ) {
      current.push(allGaps[i]);
    } else {
      clusters.push(current);
      current = [allGaps[i]];
    }
  }
  if (current.length > 0) clusters.push(current);

  const clustered = clusters.map((group) =>
    Math.round(group.reduce((a, b) => a + b, 0) / group.length)
  );

  return Array.from(new Set([0, ...clustered])).sort((a, b) => a - b);
}

function extractTableFeatures(block: TableLine[]): TableFeatures | null {
  const numRows = block.length;
  const positions = computeColumnPositions(block);
  const numColumns = positions.length;

  if (numRows === 0 || numColumns === 0) return null;

  const columnCounts = block.map((line) => {
    return positions.reduce((count, pos, i) => {
      const start = pos;
      const end = positions[i + 1] || line.text.length;
      const cell = line.text.slice(start, end).trim();
      return count + (cell ? 1 : 0);
    }, 0);
  });

  const rowCounts = Array(numColumns)
    .fill(0)
    .map((_, i) => {
      return block.reduce((count, line) => {
        const start = positions[i];
        const end = positions[i + 1] || line.text.length;
        const cell = line.text.slice(start, end).trim();
        return count + (cell ? 1 : 0);
      }, 0);
    });

  const tokenCounts = block.map((line) => line.text.trim().split(/\s+/).length);
  const avgTokens = tokenCounts.reduce((a, b) => a + b, 0) / numRows;

  const columnTokenCounts = Array(numColumns)
    .fill(0)
    .map((_, col) => {
      return block.reduce((sum, line) => {
        const start = positions[col];
        const end = positions[col + 1] || line.text.length;
        const tokens = line.text.slice(start, end).trim().split(/\s+/);
        return sum + tokens.length;
      }, 0);
    });

  const avgTokensPerColumn =
    columnTokenCounts.reduce((a, b) => a + b, 0) / numColumns;

  const singleTokenRows = tokenCounts.filter((count) => count === 1).length;
  const percentSingleTokenRows = (singleTokenRows / numRows) * 100;

  const numericOnlyRows = block.filter((line) =>
    /^\s*\d+(\s+\d+)*\s*$/.test(line.text)
  ).length;
  const percentNumericOnlyRows = (numericOnlyRows / numRows) * 100;

  const numericPrefixRows = block.filter((line) =>
    /^\s*\d+/.test(line.text)
  ).length;
  const percentNumericPrefix = (numericPrefixRows / numRows) * 100;

  const doubleSpaceCounts = block.map(
    (line) => (line.text.match(/\s{2,}/g) || []).length
  );
  const avgDoubleSpaces =
    doubleSpaceCounts.reduce((a, b) => a + b, 0) / numRows;

  const wordFreq: Record<string, number> = {};
  const consistentWords: string[] = [];

  for (let col = 0; col < numColumns; col++) {
    const columnCells = block.map((line) => {
      const start = positions[col];
      const end = positions[col + 1] || line.text.length;
      return line.text.slice(start, end).trim();
    });

    const colWordCount: Record<string, number> = {};

    columnCells.forEach((cell) => {
      const words = new Set(cell.split(/\s+/));
      words.forEach((word) => {
        if (word) colWordCount[word] = (colWordCount[word] || 0) + 1;
      });
    });

    for (const word in colWordCount) {
      if (colWordCount[word] === numRows) consistentWords.push(word);
      wordFreq[word] = Math.max(wordFreq[word] || 0, colWordCount[word]);
    }
  }

  const rowWordFreq: Record<string, number> = {};
  block.forEach((line) => {
    const words = line.text.trim().split(/\s+/);
    const freq: Record<string, number> = {};
    words.forEach((word) => {
      if (word) freq[word] = (freq[word] || 0) + 1;
    });
    for (const word in freq) {
      rowWordFreq[word] = Math.max(rowWordFreq[word] || 0, freq[word]);
    }
  });

  let mostFreqWord: string | null = null;
  let maxFreq = 0;
  for (const word in wordFreq) {
    const isNumeric = /^[\d.,/:;-]+$/.test(word);
    if (!isNumeric && wordFreq[word] > maxFreq) {
      mostFreqWord = word;
      maxFreq = wordFreq[word];
    }
  }

  let maxRowFreq = 0;
  for (const word in rowWordFreq) {
    maxRowFreq = Math.max(maxRowFreq, rowWordFreq[word]);
  }

  const percentSingleTokenCols =
    (columnTokenCounts.filter((count) => count / numRows <= 1).length /
      numColumns) *
    100;

  const percentNumericOnlyCols =
    (columnTokenCounts.filter((_, i) => {
      return block.every((line) => {
        const start = positions[i];
        const end = positions[i + 1] || line.text.length;
        return /^\s*\d+(\s+\d+)*\s*$/.test(line.text.slice(start, end).trim());
      });
    }).length /
      numColumns) *
    100;

  const percentColsWithNumericPrefix =
    (columnTokenCounts.filter((_, i) => {
      return block.every((line) => {
        const start = positions[i];
        const end = positions[i + 1] || line.text.length;
        return /^\s*\d+/.test(line.text.slice(start, end).trim());
      });
    }).length /
      numColumns) *
    100;

  return {
    num_rows: numRows,
    num_columns: numColumns,
    avg_tokens_per_row: avgTokens,
    avg_tokens_per_column: avgTokensPerColumn,
    std_dev_column_count: standardDeviation(columnCounts),
    std_dev_row_count: standardDeviation(rowCounts),
    percent_single_token_rows: percentSingleTokenRows,
    percent_single_token_columns: percentSingleTokenCols,
    percent_numeric_only_rows: percentNumericOnlyRows,
    percent_numeric_only_columns: percentNumericOnlyCols,
    percent_rows_with_numeric_prefix: percentNumericPrefix,
    percent_columns_with_numeric_prefix: percentColsWithNumericPrefix,
    avg_double_space_count_per_row: avgDoubleSpaces,
    columns_with_consistent_word: consistentWords.length,
    rows_with_consistent_word: 0,
    most_frequent_keyword: mostFreqWord,
    max_word_freq_in_column: maxFreq,
    max_word_freq_in_row: maxRowFreq,
  };
}

function flattenFeatures(featureObj: TableFeatures | null): number[] | null {
  if (!featureObj) return null;
  return [
    featureObj.num_rows,
    featureObj.num_columns,
    featureObj.avg_tokens_per_row,
    featureObj.avg_tokens_per_column,
    featureObj.std_dev_column_count,
    featureObj.std_dev_row_count,
    featureObj.percent_single_token_rows,
    featureObj.percent_single_token_columns,
    featureObj.percent_numeric_only_rows,
    featureObj.percent_numeric_only_columns,
    featureObj.percent_rows_with_numeric_prefix,
    featureObj.percent_columns_with_numeric_prefix,
    featureObj.avg_double_space_count_per_row,
    featureObj.columns_with_consistent_word,
    featureObj.rows_with_consistent_word,
    featureObj.max_word_freq_in_column,
    featureObj.max_word_freq_in_row,
  ];
}

function standardDeviation(arr: number[]): number {
  const mean = arr.reduce((a, b) => a + b, 0) / arr.length;
  return Math.sqrt(
    arr.reduce((sum, n) => sum + Math.pow(n - mean, 2), 0) / arr.length
  );
}

function CountOccurrences(
  block: TableLine[],
  positions: number[]
): {
  matched: boolean;
  combos: Array<{ combo: string; count: number; col?: number }>;
} {
  if (!block.length || !positions.length) return { matched: false, combos: [] };

  const MIN_ALNUM = 3;
  const hasLetter = (s: string) => /\p{L}/u.test(s);
  const normCell = (s: string) => s.replace(/\s+/g, ' ').trim();
  const alnumCount = (s: string) => (s.match(/[A-Za-z0-9]/g) || []).length;

  const candidatesForCell = (cell: string): string[] => {
    const c = normCell(cell);
    if (!c) return [];

    const toks = c.split(' ').filter(Boolean);
    const out = new Set<string>();

    if (alnumCount(c) >= MIN_ALNUM) out.add(c);

    for (let i = 0; i < toks.length; i++) {
      let span = '';
      for (let j = i; j < toks.length; j++) {
        span = span ? `${span} ${toks[j]}` : toks[j];
        if (alnumCount(span) >= MIN_ALNUM) out.add(span);
      }
    }
    return [...out];
  };

  const all = new Map<string, { combo: string; count: number; col: number }>();
  for (let col = 0; col < positions.length; col++) {
    const cells = block
      .map((line) => {
        const start = positions[col];
        const end = positions[col + 1] ?? line.text.length;
        return normCell(line.text.slice(start, end));
      })
      .filter(Boolean);

    if (cells.length < 2) continue;

    const freq = new Map<string, number>();

    for (const cell of cells) {
      const cands = new Set(candidatesForCell(cell));
      for (const cand of cands) {
        if (alnumCount(cand) < MIN_ALNUM) continue;
        if (!hasLetter(cand)) continue;
        freq.set(cand, (freq.get(cand) || 0) + 1);
      }
    }

    for (const [cand, count] of freq.entries()) {
      if (count < 2) continue;
      all.set(`${cand}@@${col}`, { combo: cand, count, col });
    }
  }

  const combos = [...all.values()].sort(
    (a, b) => b.count - a.count || b.combo.length - a.combo.length
  );

  return { matched: combos.length > 0, combos };
}

function countOccurrencesFromTextContent(textContent: string) {
  const text = textContent.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  const tokens = text
    .split(/[^A-Za-z0-9\p{L}\-_]+/u)
    .map((t) => t.trim())
    .filter(Boolean);

  const hasLetter = (s: string) => /\p{L}/u.test(s);
  const isNumericOnly = (s: string) => /^\d+([.,]\d+)?$/.test(s);
  const freq = new Map<string, number>();
  const add = (s: string) => {
    const v = s.trim();
    if (v.length < 3) return;
    if (!hasLetter(v)) return;
    if (isNumericOnly(v)) return;
    freq.set(v, (freq.get(v) || 0) + 1);
  };

  for (let i = 0; i < tokens.length; i++) {
    add(tokens[i]);
    if (i + 1 < tokens.length) add(`${tokens[i]} ${tokens[i + 1]}`);
  }

  return [...freq.entries()]
    .filter(([, c]) => c >= 2)
    .sort((a, b) => b[1] - a[1] || b[0].length - a[0].length)
    .map(([combo, count]) => ({ combo, count }));
}

async function hasRepeatingTable(args: {
  textContent: string;
  extractWithLLM: UtilsApi['extractWithLLM'];
}): Promise<{
  hasRepeatingTable: boolean;
  reason: string;
}> {
  const { textContent, extractWithLLM } = args;

  const clipped = textContent.slice(0, 60000);

  try {
    const response = await extractWithLLM(clipped, 'Iterator_Exists');
    if (response.isErr()) {
      return {
        hasRepeatingTable: false,
        reason: response.error.message,
      };
    }

    const text = await response.value.text;

    const start = text.indexOf('{');
    const end = text.lastIndexOf('}');
    const parsed = JSON.parse(
      start !== -1 && end !== -1 ? text.slice(start, end + 1) : text
    );

    return {
      hasRepeatingTable: Boolean(parsed?.hasRepeatingTable),
      reason: typeof parsed?.reason === 'string' ? parsed.reason : '',
    };
  } catch (e) {
    return {
      hasRepeatingTable: false,
      reason: (e as Error).message,
    };
  }
}
